import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { setPostLoginRedirect } from '@/services/post-login-redirect';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useDesktopIdleReload } from '@/hooks/useDesktopIdleReload';
import { useNotifications } from '@/hooks/useNotifications';
import { useNotificationHandler } from '@/hooks/useNotificationHandler';
import { useVMANotifications } from '@/hooks/useVMANotifications';
import { useExpirationNotifications } from '@/hooks/useExpirationNotifications';
import { useYearWheelNotifications } from '@/hooks/useYearWheelNotifications';
// Note: Polling hooks below are used as fallback for web users without push tokens
// Mobile app users (with push tokens) get notifications from backend instead
import { useGroupMembershipRequestNotifications } from '@/hooks/useGroupMembershipRequestNotifications';
import { useGroupMembershipDecisionNotifications } from '@/hooks/useGroupMembershipDecisionNotifications';
import { useGroupInvitationNotifications } from '@/hooks/useGroupInvitationNotifications';
import { useGroupActivityNotifications } from '@/hooks/useGroupActivityNotifications';
import { useFamilyChatNotifications } from '@/hooks/useFamilyChatNotifications';
import { usePushTokenRegistration } from '@/hooks/usePushTokenRegistration';
import { pushTokenLogger } from '@/utils/pushTokenLogger';
import { logPageView } from '@/lib/traffic-logger';
import Layout from '@/components/layout/Layout';
import OfflineIndicator from '@/components/OfflineIndicator';
import { Loader2 } from 'lucide-react';

// Pages
import HomePage from '@/pages/HomePage';
// LoginPage removed - we only use Entra B2C authentication
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import OnboardingPage from '@/pages/OnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import ChecklistsPage from '@/pages/ChecklistsPage';
import InventoryPage from '@/pages/InventoryPage';
import WaterPage from '@/pages/WaterPage';
import ShoppingListPage from '@/pages/ShoppingListPage';
import KnowledgePage from '@/pages/KnowledgePage';
import VmaPage from '@/pages/VmaPage';
import SettingsPage from '@/pages/SettingsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import GroupsPage from '@/pages/GroupsPage';
import GroupDetailPage from '@/pages/GroupDetailPage';
import GroupEntryPage from '@/pages/GroupEntryPage';
import GroupNotificationsPage from '@/pages/GroupNotificationsPage';
import GroupSearchPage from '@/pages/GroupSearchPage';
import FamilyPage from '@/pages/FamilyPage';
import YearWheelPage from '@/pages/YearWheelPage';
import NotesPage from '@/pages/NotesPage';
import RecipesPage from '@/pages/RecipesPage';
import NotificationTestPage from '@/pages/NotificationTestPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import CookiesPage from '@/pages/CookiesPage';
import CommunityGuidelinesPage from '@/pages/CommunityGuidelinesPage';
import AboutPage from '@/pages/AboutPage';
import PricingPage from '@/pages/PricingPage';
import DeleteAccountPage from '@/pages/DeleteAccountPage';
import SupportPage from '@/pages/SupportPage';
import ToastProvider from '@/components/ToastProvider';
import InvitePrompt from '@/components/InvitePrompt';
import FamilyRemovalNotification from '@/components/FamilyRemovalNotification';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, _hasHydrated, user } = useAuthStore();
  const location = useLocation();

  // Note: do not enforce prepGoal here. The settings-store handles downgrade
  // behavior reactively to avoid overwriting server values when the app
  // initializes on another device.

  // Vänta på att auth-state har laddats från localStorage
  if (!_hasHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (location.pathname.startsWith('/app/groups/')) {
      const redirectPath = `${location.pathname}${location.search}${location.hash}`;
      setPostLoginRedirect(redirectPath);
    }
    return <Navigate to="/" replace />;
  }

  // Redirect till onboarding om inte slutförd
  if (user && !user.onboardingCompleted && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Starta push token logger vid appstart
pushTokenLogger.start();

function App() {
  const location = useLocation();

  // Log page views for traffic analytics
  useEffect(() => {
    logPageView({
      app: 'web',
      path: location.pathname,
    });

    // Återställ sidtitel när man navigerar till startsidan, t.ex. från publika
    // sidor som "Om oss". HomePage sätter titeln i sin egen useEffect men den
    // körs asynkront efter render – vid snabb navigation kan titeln från den
    // föregående sidan ligga kvar. Här garanterar vi att titeln alltid
    // återställs direkt vid navigering till /.
    if (location.pathname === '/') {
      document.title = 'Beredskapshubben - Sveriges beredskapsapp';
    }
  }, [location.pathname]);

  // Sätt upp automatisk token refresh
  useTokenRefresh();

  // Desktop browser: ladda om sidan efter längre inaktivitet för att undvika stale session/CORS
  useDesktopIdleReload();

  // Initialize push token registration
  usePushTokenRegistration();

  // Initialize notifications
  useNotifications();
  
  // Handle notification clicks
  useNotificationHandler();
  
  // Setup notification triggers
  useVMANotifications();
  useExpirationNotifications();
  useYearWheelNotifications();
  
  // Group and family notifications:
  // - Mobile app users: Get push notifications from backend
  // - Web users: Get notifications from polling hooks below (they don't have push tokens)
  useFamilyChatNotifications();
  useGroupMembershipRequestNotifications();
  useGroupMembershipDecisionNotifications();
  useGroupInvitationNotifications();
  useGroupActivityNotifications();

  return (
    <>
      <OfflineIndicator />
      <ToastProvider />
      <InvitePrompt />
      <FamilyRemovalNotification />
      <Routes>
        {/* Publika routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/groups/:groupId" element={<GroupEntryPage />} />
        {/* /login route removed - we only use Entra B2C */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/vma" element={<VmaPage />} />
        
        {/* Legal pages - public, indexable */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Skyddade routes med layout */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="checklists" element={<ChecklistsPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="water" element={<WaterPage />} />
        <Route path="shopping-list" element={<ShoppingListPage />} />
        <Route path="recipes" element={<RecipesPage />} />
        <Route path="knowledge" element={<KnowledgePage />} />
        <Route path="vma" element={<VmaPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="family" element={<FamilyPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="yearwheel" element={<YearWheelPage />} />
        <Route path="notes" element={<NotesPage />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="groups/search" element={<GroupSearchPage />} />
        <Route path="groups/notifications" element={<GroupNotificationsPage />} />
        <Route path="groups/:groupId" element={<GroupDetailPage />} />
        <Route path="admin/notification-test" element={<NotificationTestPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  );
}

export default App;
